/**
 * Notifications — everything we have told this borrower.
 *
 * The point is recall: a customer who missed an SMS, or was told something
 * months ago, can read the exact words again. So nothing here is a summary —
 * each row is the message as it was sent, with the date it was sent.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Banknote, Bell, CheckCheck, CreditCard, FileText, Loader2, ShieldCheck, Trash2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { platformApi } from "@/platform/api";

type Row = {
  id: string; category: string; title: string; body: string;
  entity_type: string | null; entity_id: string | null;
  read: boolean; created_at: string | null;
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "application", label: "Applications" },
  { key: "disbursement", label: "Money out" },
  { key: "repayment", label: "Repayments" },
  { key: "reminder", label: "Reminders" },
  { key: "account", label: "Account" },
];

const ICONS: Record<string, typeof Bell> = {
  application: FileText,
  disbursement: Banknote,
  repayment: CreditCard,
  reminder: Bell,
  account: ShieldCheck,
};

/** "2 hours ago" beats a timestamp for recent things, which is most of them. */
function whenLabel(iso: string | null) {
  if (!iso) return "";
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return then.toLocaleDateString();
}

export function NotificationsPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await platformApi.notifications(
        filter ? `?category=${filter}` : "");
      setRows(data.notifications);
      setUnread(data.unread);
    } catch (error) {
      toast({
        title: "Could not load your messages",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [filter, toast]);

  useEffect(() => { load(); }, [load]);

  // Opening the page is the act of reading them; mark them read once the list
  // has actually been shown rather than making the user click a button.
  useEffect(() => {
    if (!loading && unread > 0) {
      platformApi.markNotificationsRead()
        .then(() => {
          setUnread(0);
          window.dispatchEvent(new Event("notifications:changed"));
        })
        .catch(() => undefined);
    }
  }, [loading, unread]);

  const dismiss = async (id: string) => {
    setRows((current) => current.filter((r) => r.id !== id));
    try {
      await platformApi.dismissNotification(id);
      window.dispatchEvent(new Event("notifications:changed"));
    } catch {
      load(); // put it back if the server disagreed
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Notifications</h1>
          <p className="text-sm text-muted-foreground">
            Everything we have sent you, newest first.
          </p>
        </div>
        {unread > 0 && (
          <Button variant="outline" size="sm" onClick={async () => {
            await platformApi.markNotificationsRead();
            setUnread(0);
            window.dispatchEvent(new Event("notifications:changed"));
            load();
          }}>
            <CheckCheck className="mr-1 h-4 w-4" />Mark all read
          </Button>
        )}
      </header>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button key={f.key} size="sm"
            variant={filter === f.key ? "default" : "outline"}
            onClick={() => setFilter(f.key)}>
            {f.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin" />
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Bell className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">
              {filter
                ? "Nothing in this category yet."
                : "No messages yet. We will let you know when something happens with your loan."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((n) => {
            const Icon = ICONS[n.category] || Bell;
            return (
              <Card key={n.id} className={n.read ? undefined : "border-primary/40"}>
                <CardContent className="flex gap-3 py-4">
                  <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    n.read ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium">{n.title}</p>
                      <div className="flex shrink-0 items-center gap-2">
                        {!n.read && (
                          <Badge className="bg-primary/10 text-primary">new</Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {whenLabel(n.created_at)}
                        </span>
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{n.body}</p>
                    <div className="mt-2 flex items-center gap-3">
                      {n.entity_type === "loan" && n.entity_id && (
                        <Link to="/dashboard/loans"
                          className="text-xs text-primary hover:underline">
                          View my loan
                        </Link>
                      )}
                      {n.entity_type === "kyc" && (
                        <Link to="/dashboard/kyc"
                          className="text-xs text-primary hover:underline">
                          Go to verification
                        </Link>
                      )}
                      <button onClick={() => dismiss(n.id)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-3 w-3" />Remove
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
